/**
 * guiaAyudas.js
 * Diccionario centralizado de ayuda contextual para la plataforma tresaedu.
 * Estructurado por rutas para detectar automáticamente la sección activa y mostrar
 * resúmenes, funcionalidades clave, esquemas lógicos y consejos personalizados por rol.
 */

export const AYUDAS_POR_RUTA = [
  {
    patrones: ['/Principal', '/dashboard'],
    titulo: 'Panel Principal',
    subtitulo: 'Centro de control y guía del sistema',
    icono: 'fa-house',
    resumen: 'Espacio principal donde se concentran los accesos directos, avisos recientes, actividades destacadas e información del ciclo lectivo.',
    funcionalidades: [
      {
        titulo: 'Tarjetas de Acceso Rápido',
        descripcion: 'Ingresa directamente a tus cursos, mensajes pendientes o calendario de eventos.',
        icono: 'fa-bolt'
      },
      {
        titulo: 'Avisos y Novedades',
        descripcion: 'Visualiza comunicados institucionales y notificaciones importantes.',
        icono: 'fa-bullhorn'
      },
      {
        titulo: 'Resumen de Actividades',
        descripcion: 'Consulta las tareas, eventos o fechas importantes de la agenda.',
        icono: 'fa-clock-rotate-left'
      }
    ],
    pasosIniciales: [
      {
        paso: 1,
        titulo: 'Estructura Curricular',
        descripcion: 'Crear o revisar la oferta y estructura curricular.',
        icono: 'fa-box-archive',
        ruta: '/Formaciones'
      },
      {
        paso: 2,
        titulo: 'Crear Cohorte',
        descripcion: 'Crear las cohortes activas para el ciclo lectivo.',
        icono: 'fa-boxes-stacked',
        ruta: '/Cursos'
      },
      {
        paso: 3,
        titulo: 'Instancias de Calificación',
        descripcion: 'Seleccionar las instancias de valoración y evaluación.',
        icono: 'fa-list-check',
        ruta: '/Cursos'
      },
      {
        paso: 4,
        titulo: 'Docentes y Estudiantes',
        descripcion: 'Asignar docente/s (o exportar de años anteriores) y agregar/promover estudiantes.',
        icono: 'fa-user-tie',
        ruta: '/Cursos'
      },
      {
        paso: 5,
        titulo: 'Reportes y Listados',
        descripcion: 'Consultar, imprimir o exportar listados a Excel.',
        icono: 'fa-chart-pie',
        ruta: '/Reportes'
      },
      {
        paso: 6,
        titulo: 'Administrar Horarios',
        descripcion: 'Organizar la grilla horaria de los espacios curriculares.',
        icono: 'fa-clock',
        ruta: '/Horarios'
      }
    ],
    porRol: {
      admin: {
        titulo: 'Panel Principal — Administración',
        subtitulo: 'Centro de control y guía de inicio del sistema',
        resumen: 'Espacio principal donde se concentran los accesos directos, avisos recientes, actividades destacadas y el esquema de inicio para la configuración institucional.',
        funcionalidades: [
          {
            titulo: 'Tarjetas de Acceso Rápido',
            descripcion: 'Ingresa directamente a la gestión de cursos, usuarios, reportes y configuración.',
            icono: 'fa-bolt'
          },
          {
            titulo: 'Avisos y Novedades Institucionales',
            descripcion: 'Publica y visualiza comunicados dirigidos a toda la comunidad educativa.',
            icono: 'fa-bullhorn'
          },
          {
            titulo: 'Resumen de Asistencias y Actividades',
            descripcion: 'Monitorea el estado de asistencias generales, alertas del sistema y tareas vigentes.',
            icono: 'fa-chart-line'
          }
        ],
        pasosIniciales: [
          { paso: 1, titulo: 'Estructura Curricular', descripcion: 'Crear o revisar la oferta y estructura curricular.', icono: 'fa-box-archive', ruta: '/Formaciones' },
          { paso: 2, titulo: 'Crear Cohorte', descripcion: 'Crear las cohortes activas para el ciclo lectivo.', icono: 'fa-boxes-stacked', ruta: '/Cursos' },
          { paso: 3, titulo: 'Instancias de Calificación', descripcion: 'Seleccionar las instancias de valoración y evaluación.', icono: 'fa-list-check', ruta: '/Cursos' },
          { paso: 4, titulo: 'Docentes y Estudiantes', descripcion: 'Asignar docente/s (o exportar de años anteriores) y agregar/promover estudiantes.', icono: 'fa-user-tie', ruta: '/Cursos' },
          { paso: 5, titulo: 'Reportes y Listados', descripcion: 'Consultar, imprimir o exportar listados a Excel.', icono: 'fa-chart-pie', ruta: '/Reportes' },
          { paso: 6, titulo: 'Administrar Horarios', descripcion: 'Organizar la grilla horaria de los espacios curriculares.', icono: 'fa-clock', ruta: '/Horarios' }
        ]
      },
      docente: {
        titulo: 'Panel Principal — Docente',
        subtitulo: 'Espacios curriculares a cargo, avisos y agenda de eventos',
        resumen: 'Pantalla principal para docentes donde encuentras el listado de materias y cursos que dictas, el acceso directo a cada aula virtual, los comunicados institucionales y la agenda de eventos.',
        funcionalidades: [
          {
            titulo: 'Espacios Curriculares a Cargo',
            descripcion: 'Accede a las materias asignadas para gestionar aulas virtuales, subir contenidos, registrar asistencias, asignar tareas y evaluar alumnos.',
            icono: 'fa-chalkboard-user'
          },
          {
            titulo: 'Mensajes y Comunicados',
            descripcion: 'Revisa las notificaciones y mensajes recibidos de estudiantes, preceptores o directivos.',
            icono: 'fa-envelope'
          },
          {
            titulo: 'Agenda y Recordatorios de Eventos',
            descripcion: 'Consulta las fechas destacadas en tu calendario escolar, reuniones institucionales y mesas de examen.',
            icono: 'fa-calendar-days'
          }
        ],
        pasosIniciales: null
      },
      estudiante: {
        titulo: 'Panel Principal — Estudiante',
        subtitulo: 'Año de cursado, asistencia, espacios por día, docentes y eventos',
        resumen: 'Pantalla principal donde puedes consultar el año que cursas, el registro de asistencia, los espacios curriculares organizados por día, la lista de docentes por espacio y la agenda de eventos actuales y próximos.',
        funcionalidades: [
          {
            titulo: 'Año de Cursado y Asistencia',
            descripcion: 'Visualiza el año lectivo que estás cursando y tu registro de asistencias.',
            icono: 'fa-graduation-cap'
          },
          {
            titulo: 'Espacios por Día y Docentes por Espacio',
            descripcion: 'Consulta los espacios curriculares ordenados por día de la semana y los docentes a cargo de cada espacio.',
            icono: 'fa-calendar-day'
          },
          {
            titulo: 'Eventos Actuales y Próximos',
            descripcion: 'Mantente informado sobre los eventos actuales, fechas de exámen y actividades escolares próximas.',
            icono: 'fa-calendar-check'
          }
        ],
        pasosIniciales: null
      },
      tutor: {
        titulo: 'Panel Principal — Tutor',
        subtitulo: 'Selección de tutelado, información general, calificaciones y agenda',
        resumen: 'Portal para tutores donde puedes seleccionar el estudiante tutelado a tu cargo para consultar su información general y sus calificaciones, así como acceder a la agenda institucional del tutor.',
        funcionalidades: [
          {
            titulo: 'Selección del Tutelado',
            descripcion: 'Selecciona al estudiante a cargo para visualizar su información general y dar seguimiento a su trayecto escolar.',
            icono: 'fa-user-graduate'
          },
          {
            titulo: 'Información General y Calificaciones',
            descripcion: 'Consulta los datos del estudiante tutelado y el detalle de sus calificaciones publicadas y asistencias.',
            icono: 'fa-graduation-cap'
          },
          {
            titulo: 'Agenda Institucional del Tutor',
            descripcion: 'Consulta el calendario escolar con fechas de reuniones de tutores, actos, exámenes y recesos lectivos.',
            icono: 'fa-calendar-days'
          }
        ],
        pasosIniciales: null
      },
      tutor_tutelado: {
        titulo: 'Panel Principal — Vista de Estudiante (Modo Tutor)',
        subtitulo: 'Navegación de materias e información del alumno a cargo',
        resumen: 'Te encuentras dentro de la vista del estudiante tutelado consultando sus materias, publicaciones y avance académico. Puedes cerrar esta vista en cualquier momento utilizando el botón/barra superior para regresar al Panel Principal del Tutor.',
        funcionalidades: [
          {
            titulo: 'Vista del Estudiante Tutelado',
            descripcion: 'Estás navegando la pantalla del alumno a tu cargo para revisar sus asignaturas, contenidos y rendimiento.',
            icono: 'fa-user-graduate'
          },
          {
            titulo: 'Cerrar Vista y Volver al Panel de Tutor',
            descripcion: 'Utiliza el botón o barra de cierre en la parte superior para finalizar la vista de estudiante y regresar a tu Panel Principal de Tutor.',
            icono: 'fa-xmark'
          }
        ],
        pasosIniciales: null
      },
      preceptor: {
        titulo: 'Panel Principal — Preceptoría',
        subtitulo: 'Control de asistencias, divisiones y avisos',
        resumen: 'Centro de trabajo para la gestión de la asistencia diaria de tus divisiones, emisión de avisos y seguimiento de estudiantes.',
        funcionalidades: [
          {
            titulo: 'Toma y Registro de Asistencias',
            descripcion: 'Controla el presente/ausente diario por comisión y división a tu cargo.',
            icono: 'fa-clipboard-user'
          },
          {
            titulo: 'Comunicados a Cursos',
            descripcion: 'Envía notificaciones e informes a estudiantes, tutores o docentes de tus comisiones.',
            icono: 'fa-bullhorn'
          },
          {
            titulo: 'Calendario Escolar',
            descripcion: 'Supervisa la agenda de actividades y feriados lectivos.',
            icono: 'fa-calendar-week'
          }
        ],
        pasosIniciales: null
      },
      tesoreria: {
        titulo: 'Panel Principal — Tesorería',
        subtitulo: 'Gestión de aranceles y pagos',
        resumen: 'Pantalla de acceso rápido para la administración de cobranzas de matrícula, cuotas mensuales y saldos arancelarios.',
        funcionalidades: [
          {
            titulo: 'Administración de Cuotas',
            descripcion: 'Consulta y registra cobros por estudiante y división.',
            icono: 'fa-receipt'
          }
        ],
        pasosIniciales: null
      }
    },
    consejosPorRol: {
      admin: 'Sigue el esquema lógico de 6 pasos al inicio de cada ciclo lectivo para poner en marcha el sistema sin omisiones.',
      docente: 'Accede a cada espacio curricular asignado para subir contenidos, registrar asistencias y gestionar calificaciones.',
      estudiante: 'Mantén un ojo en las fechas límite de entrega de tareas y revisa los nuevos mensajes recibidos.',
      preceptor: 'Verifica la asistencia diaria de tus divisiones y consulta los comunicados institucionales.'
    }
  },
  {
    patrones: ['/Formaciones'],
    titulo: 'Estructura Curricular y Oferta Académica',
    subtitulo: 'Organización estructural de carreras y espacios curriculares',
    icono: 'fa-box-archive',
    resumen: 'Módulo dedicado exclusivamente a organizar la oferta educativa, definir la estructura de carreras o niveles y gestionar los espacios curriculares de manera estructural (en esta sección NO se crean cohortes ni se matriculan alumnos).',
    funcionalidades: [
      {
        titulo: 'Ofertas Educativas',
        descripcion: 'Crea y edita carreras, especializaciones o niveles académicos.',
        icono: 'fa-folder-plus'
      },
      {
        titulo: 'Estructura por Años',
        descripcion: 'Define la cantidad de años, planes de estudio y niveles de cursado.',
        icono: 'fa-sitemap'
      },
      {
        titulo: 'Espacios Curriculares Estructurales',
        descripcion: 'Crea y vincula las materias que componen formalmente el plan de estudios.',
        icono: 'fa-link'
      }
    ],
    consejosPorRol: {
      admin: 'Configura la estructura curricular base antes de pasar al módulo de Cursos a dar de alta las cohortes.',
      docente: 'Consulta esta sección para revisar el plan de estudio oficial y las asignaturas de tu especialidad.'
    }
  },
  {
    patrones: ['/Cursos'],
    titulo: 'Cursos y Cohortes',
    subtitulo: 'Vista general de materias por formación y cohorte activa',
    icono: 'fa-boxes-stacked',
    resumen: 'Vista general de todos los cursos y materias organizados por formación y cohorte activa. Permite crear cohortes, administrar y asignar docentes a cargo (o repetir la planta docente de años anteriores), configurar las instancias de valoración/calificación y agregar nuevos grupos de alumnos.',
    funcionalidades: [
      {
        titulo: 'Creación de Cohortes',
        descripcion: 'Da de alta la cohorte correspondiente al año o ciclo lectivo.',
        icono: 'fa-layer-group'
      },
      {
        titulo: 'Planta Docente',
        descripcion: 'Asigna profesores a cada materia o repite la planta docente de años anteriores.',
        icono: 'fa-user-tie'
      },
      {
        titulo: 'Instancias de Calificación',
        descripcion: 'Define las instancias de valoración (parciales, finales, informes) por curso.',
        icono: 'fa-list-check'
      },
      {
        titulo: 'Nuevos Grupos y Comisiones',
        descripcion: 'Agrega comisiones o grupos de cursado para organizar la matrícula.',
        icono: 'fa-users-rectangle'
      }
    ],
    consejosPorRol: {
      admin: 'Utiliza la opción de repetir planta docente de años anteriores para agilizar el armado de comisiones en segundos.',
      docente: 'Ubica tus comisiones asignadas para verificar qué instancias de calificación están vigentes.'
    }
  },
  {
    patrones: ['/MC/*/l', '/MC/l', '/laboratorio'],
    titulo: 'Laboratorio de Práctica y Evaluaciones',
    subtitulo: 'Centro de creación de actividades, cuestionarios, rúbricas y trayectorias',
    icono: 'fa-flask',
    resumen: 'Espacio de trabajo pedagógico interactivo para el diseño y administración de actividades prácticas, cuestionarios con opción a Inteligencia Artificial, rúbricas de evaluación y registro cualitativo de trayectorias.',
    funcionalidades: [
      {
        titulo: 'Actividades de Práctica',
        descripcion: 'Crea tareas prácticas con consignas, archivos adjuntos, fechas límite y corrección de entregas.',
        icono: 'fa-pen-to-square'
      },
      {
        titulo: 'Cuestionarios e Inteligencia Artificial',
        descripcion: 'Diseña exámenes interactivos (Opción Múltiple, V/F) y genera preguntas automáticas mediante asistencias de IA.',
        icono: 'fa-brain'
      },
      {
        titulo: 'Rúbricas de Evaluación',
        descripcion: 'Define criterios e indicadores cualitativos y cuantitativos para evaluaciones estandarizadas.',
        icono: 'fa-table-list'
      },
      {
        titulo: 'Registro de Trayectorias',
        descripcion: 'Redacta informes pedagógicos cualitativos sobre el avance formativo de cada estudiante.',
        icono: 'fa-route'
      }
    ],
    porRol: {
      docente: {
        titulo: 'Laboratorio de Práctica — Panel Docente',
        subtitulo: 'Herramientas pedagógicas, evaluaciones e IA del curso',
        resumen: 'Pantalla principal de trabajo docente en el laboratorio de la materia. Desde aquí puedes crear y gestionar actividades prácticas, diseñar cuestionarios interactivos con asistencia de IA, para luego subirlas a las clases. También configurar rúbricas de evaluación y preparar los informes de trayectorias educativas.',
        funcionalidades: [
          {
            titulo: 'Actividades de Práctica',
            descripcion: 'Configura consignas de clase, adjunta archivos explicativos, define límites de entrega y realiza las correcciones individuales de tus estudiantes.',
            icono: 'fa-file-circle-plus'
          },
          {
            titulo: 'Cuestionarios (+ IA)',
            descripcion: 'Crea autoevaluaciones y exámenes con corrección automática (multiple choice) o utiliza el generador con Inteligencia Artificial para armar preguntas rápidamente.',
            icono: 'fa-robot'
          },
          {
            titulo: 'Rúbricas de Evaluación',
            descripcion: 'Establece matrices de criterios e indicadores de desempeño para transparente objetividad al calificar.',
            icono: 'fa-list-check'
          },
          {
            titulo: 'Registro de Trayectorias Pedagógicas',
            descripcion: 'Escribe el informe cualitativo individual sobre el recorrido y logros de cada estudiante en este espacio curricular y en cada instancia de valoración.',
            icono: 'fa-route'
          }
        ]
      },
      estudiante: {
        titulo: 'Laboratorio de Práctica — Estudiante',
        subtitulo: 'Mis actividades, exámenes y criterios de evaluación',
        resumen: 'Espacio donde puedes acceder a las consignas prácticas asignadas por tus docentes, responder cuestionarios evaluativos y consultar los criterios de evaluación.',
        funcionalidades: [
          {
            titulo: 'Actividades Asignadas',
            descripcion: 'Consulta consignas, descarga archivos adjuntos y sube las soluciones de tus trabajos prácticos.',
            icono: 'fa-upload'
          },
          {
            titulo: 'Cuestionarios Evaluativos',
            descripcion: 'Responde evaluaciones e autoevaluaciones interactivas en línea.',
            icono: 'fa-square-check'
          },
          {
            titulo: 'Criterios de Evaluación y Rúbricas',
            descripcion: 'Revisa cómo se evaluará tu desempeño en cada trabajo o proyecto.',
            icono: 'fa-clipboard-check'
          }
        ]
      }
    },
    consejosPorRol: {
      docente: 'Utiliza la generación con IA en Cuestionarios para agilizar la preparación de exámenes y configura las Rúbricas antes de calificar actividades.',
      estudiante: 'Lee atenta y detalladamente la consigna y la rúbrica antes de adjuntar y enviar la entrega de tu actividad.'
    }
  },
  {
    patrones: ['/MC/*/c', '/MC/*/c/*'],
    titulo: 'Dictado y Creación de Clases',
    subtitulo: 'Gestión de lecciones, foros y actividades vinculadas',
    icono: 'fa-chalkboard',
    resumen: 'Módulo de dictado de clases para crear clases, adjuntar actividades o cuestionarios previamente creados en laboratorio a las clases, subir material complementario a la clase y crear un foro de debate.',
    funcionalidades: [
      {
        titulo: 'Crear Clases',
        descripcion: 'Diseña y publica unidades didácticas y temas del programa.',
        icono: 'fa-book-medical'
      },
      {
        titulo: 'Adjuntar Actividades o Cuestionarios',
        descripcion: 'Vincula a la clase actividades o cuestionarios previamente creados en el laboratorio.',
        icono: 'fa-paperclip'
      },
      {
        titulo: 'Subir Material Complementario',
        descripcion: 'Sube lecturas, documentos, archivos adjuntos y presentaciones a la clase.',
        icono: 'fa-file-arrow-up'
      },
      {
        titulo: 'Crear Foro de Debate',
        descripcion: 'Abre un espacio de discusión interactivo sobre los temas tratados.',
        icono: 'fa-comments'
      }
    ],
    porRol: {
      docente: {
        titulo: 'Gestión de Clases — Panel Docente',
        subtitulo: 'Creación de clases, actividades vinculadas, material y foros',
        resumen: 'En este módulo puedes crear clases, adjuntar actividades o cuestionarios previamente creados en laboratorio a las clases, subir material complementario a la clase y crear un foro de debate.',
        funcionalidades: [
          {
            titulo: 'Crear Clases',
            descripcion: 'Diseña y publica lecciones y contenidos didácticos para el curso.',
            icono: 'fa-book-medical'
          },
          {
            titulo: 'Adjuntar Actividades o Cuestionarios del Laboratorio',
            descripcion: 'Vincula a las clases las actividades o cuestionarios previamente armados en el módulo Laboratorio.',
            icono: 'fa-thumbtack'
          },
          {
            titulo: 'Subir Material Complementario',
            descripcion: 'Comparte archivos, documentos, presentaciones y enlaces didácticos a la clase.',
            icono: 'fa-file-arrow-up'
          },
          {
            titulo: 'Crear Foro de Debate',
            descripcion: 'Abre foros de discusión interactivos para promover la participación sobre el tema de la lección.',
            icono: 'fa-comments'
          }
        ]
      }
    }
  },
  {
    patrones: ['/MC/*/e', '/MC/*/e/*'],
    titulo: 'Estudiantes del Aula Virtual',
    subtitulo: 'Lista de estudiantes, calificaciones, asistencia y actividades',
    icono: 'fa-users',
    resumen: 'Módulo de gestión de estudiantes que incluye la lista de estudiantes, Calificaciones, Asistencia y Actividades.',
    funcionalidades: [
      {
        titulo: 'Lista de Estudiantes',
        descripcion: 'Nómina de alumnos matriculados en la comisión.',
        icono: 'fa-users'
      },
      {
        titulo: 'Calificaciones',
        descripcion: 'Consulta y registro de notas y valoraciones pedagógicas.',
        icono: 'fa-star'
      },
      {
        titulo: 'Asistencia',
        descripcion: 'Control y registro de presentismo del curso.',
        icono: 'fa-clipboard-user'
      },
      {
        titulo: 'Actividades',
        descripcion: 'Seguimiento de entregas y tareas asignadas.',
        icono: 'fa-tasks'
      }
    ],
    porRol: {
      docente: {
        titulo: 'Gestión de Estudiantes — Panel Docente',
        subtitulo: 'Lista de estudiantes, Calificaciones, Asistencia y Actividades',
        resumen: 'En esta sección encuentras la lista de estudiantes del curso, sus Calificaciones, el registro de Asistencia y el seguimiento de sus Actividades.',
        funcionalidades: [
          {
            titulo: 'Lista de Estudiantes',
            descripcion: 'Consulta la nómina de alumnos inscritos en esta comisión.',
            icono: 'fa-users'
          },
          {
            titulo: 'Calificaciones',
            descripcion: 'Carga y supervisión de notas y valoraciones del alumnado.',
            icono: 'fa-star'
          },
          {
            titulo: 'Asistencia',
            descripcion: 'Toma y registro diario de asistencia por estudiante.',
            icono: 'fa-clipboard-user'
          },
          {
            titulo: 'Actividades',
            descripcion: 'Seguimiento de las entregas y correcciones realizadas.',
            icono: 'fa-tasks'
          }
        ]
      }
    }
  },
  {
    patrones: ['/MC/*'],
    titulo: 'Muro del Curso',
    subtitulo: 'Sucesos, lista de estudiantes, presentación y novedades',
    icono: 'fa-chalkboard-user',
    resumen: 'Muro del curso donde se visualiza el muro de sucesos del curso, lista de estudiantes, presentación del docente, nuevas clases, actividades, cuestionarios, etc.',
    funcionalidades: [
      {
        titulo: 'Muro de Sucesos del Curso',
        descripcion: 'Novedades, avisos y eventos cronológicos del aula virtual.',
        icono: 'fa-newspaper'
      },
      {
        titulo: 'Lista de Estudiantes',
        descripcion: 'Nómina de alumnos integrantes de la comisión.',
        icono: 'fa-users'
      },
      {
        titulo: 'Presentación del Docente',
        descripcion: 'Información institucional de bienvenida y pautas de cursado.',
        icono: 'fa-address-card'
      },
      {
        titulo: 'Novedades y Publicaciones',
        descripcion: 'Avisos directos sobre nuevas clases, actividades y cuestionarios.',
        icono: 'fa-bullhorn'
      }
    ],
    porRol: {
      docente: {
        titulo: 'Muro del Curso — Panel Docente',
        subtitulo: 'Muro de sucesos, lista de estudiantes, presentación del docente y publicaciones',
        resumen: 'En el muro de este curso se visualiza el muro de sucesos del curso, la lista de estudiantes, la presentación del docente y las publicaciones de nuevas clases, actividades, cuestionarios, etc.',
        funcionalidades: [
          {
            titulo: 'Muro de Sucesos del Curso',
            descripcion: 'Publica anuncios, noticias y comunicaciones visibles para todo el curso.',
            icono: 'fa-newspaper'
          },
          {
            titulo: 'Lista de Estudiantes',
            descripcion: 'Consulta la nómina de alumnos inscritos en esta comisión.',
            icono: 'fa-users'
          },
          {
            titulo: 'Presentación del Docente',
            descripcion: 'Edita tu mensaje de bienvenida, programa sintético y pautas de cursado.',
            icono: 'fa-address-card'
          },
          {
            titulo: 'Nuevas Clases, Actividades y Cuestionarios',
            descripcion: 'Muestra las novedades recién publicadas en el aula.',
            icono: 'fa-bullhorn'
          }
        ]
      }
    }
  },
  {
    patrones: ['/MC'],
    titulo: 'Espacios Curriculares a Cargo',
    subtitulo: 'Listado de materias asignadas',
    icono: 'fa-chalkboard-user',
    resumen: 'Espacios Curriculares a Cargo: Accede a las materias asignadas para gestionar aulas virtuales, subir contenidos, registrar asistencias, asignar tareas y evaluar alumnos.',
    funcionalidades: [
      {
        titulo: 'Espacios Curriculares a Cargo',
        descripcion: 'Accede a las materias asignadas para gestionar aulas virtuales, subir contenidos, registrar asistencias, asignar tareas y evaluar alumnos.',
        icono: 'fa-chalkboard-user'
      }
    ],
    porRol: {
      docente: {
        titulo: 'Espacios Curriculares a Cargo',
        subtitulo: 'Listado de materias asignadas',
        resumen: 'Espacios Curriculares a Cargo: Accede a las materias asignadas para gestionar aulas virtuales, subir contenidos, registrar asistencias, asignar tareas y evaluar alumnos.',
        funcionalidades: [
          {
            titulo: 'Espacios Curriculares a Cargo',
            descripcion: 'Accede a las materias asignadas para gestionar aulas virtuales, subir contenidos, registrar asistencias, asignar tareas y evaluar alumnos.',
            icono: 'fa-chalkboard-user'
          }
        ]
      },
      estudiante: {
        titulo: 'Espacios Curriculares Cursando',
        subtitulo: 'Listado de materias en cursado activo',
        resumen: 'Espacios Curriculares Cursando: Accede a tus materias activas para ingresar al aula virtual, consultar clases, tareas, materiales y publicaciones.',
        funcionalidades: [
          {
            titulo: 'Espacios Curriculares Cursando',
            descripcion: 'Accede a tus materias activas para ingresar al aula virtual, consultar clases, tareas, materiales y publicaciones.',
            icono: 'fa-book-open'
          }
        ]
      }
    }
  },
  {
    patrones: ['/Usuarios', '/Ficha', '/VerPerfil'],
    titulo: 'Gestión de Usuarios y Perfiles',
    subtitulo: 'Administración de alumnos, profesores, tutores y personal',
    icono: 'fa-users-gear',
    resumen: 'Módulo para buscar, dar de alta o modificar la información del personal docente, alumnos, tutores, preceptores y administradores.',
    funcionalidades: [
      {
        titulo: 'Búsqueda Avanzada',
        descripcion: 'Filtra usuarios por DNI, nombre, apellido, rol o estado académico.',
        icono: 'fa-magnifying-glass'
      },
      {
        titulo: 'Ficha de Usuario',
        descripcion: 'Consulta datos personales, contactos de emergencia, legajo y trayectoria.',
        icono: 'fa-id-card'
      },
      {
        titulo: 'Asignación de Roles',
        descripcion: 'Otorga o revoca permisos institucionales (Admin, Docente, Alumno, Tutor, Preceptor).',
        icono: 'fa-user-shield'
      }
    ],
    consejosPorRol: {
      admin: 'Mantén actualizados los correos de contacto para asegurar que las notificaciones institucionales lleguen a destino.',
      preceptor: 'Verifica los legajos de los estudiantes de tu división y vincula las relaciones familiares/tutores.'
    }
  },
  {
    patrones: ['/CalificacionesGeneral', '/Calificaciones', '/Recorrido'],
    titulo: 'Calificaciones General',
    subtitulo: 'Administración del ciclo de vida y estado de calificaciones',
    icono: 'fa-star',
    resumen: 'Consola centralizada para supervisar el estado de carga, publicación y visibilidad de las notas de toda la institución. Permite ver el listado completo de cursos para un panorama general o filtrar por curso e instancia.',
    procesoCalificacion: [
      {
        paso: 1,
        titulo: 'Carga Docente',
        descripcion: 'El docente ingresa la calificación del estudiante en el sistema.',
        icono: 'fa-pen'
      },
      {
        paso: 2,
        titulo: 'Aceptación Institucional',
        descripcion: 'Se acepta la calificación cargada (una vez aceptada, el docente YA NO la puede editar).',
        icono: 'fa-lock'
      },
      {
        paso: 3,
        titulo: 'Publicación Oficial',
        descripcion: 'Se publica la nota (recién a partir de esta etapa es visible para el estudiante y su tutor).',
        icono: 'fa-bullhorn'
      },
      {
        paso: 4,
        titulo: 'Visualización y Confirmación',
        descripcion: 'El estudiante y tutor visualizan la nota publicada y se registra la conformidad de vista.',
        icono: 'fa-eye'
      }
    ],
    funcionalidades: [
      {
        titulo: 'Panorama General por Cursos',
        descripcion: 'Monitorea de forma global el porcentaje de avance en la carga y publicación de notas.',
        icono: 'fa-table-list'
      },
      {
        titulo: 'Vista por Curso e Instancia',
        descripcion: 'Detalle fino por asignatura, trimestre o examen final.',
        icono: 'fa-filter'
      },
      {
        titulo: 'Proceso de Aprobación Inmutable',
        descripcion: 'Control estricto que impide modificaciones docentes posteriores a la aceptación.',
        icono: 'fa-shield-halved'
      }
    ],
    consejosPorRol: {
      admin: 'Revisa periódicamente el estado de Carga antes de proceder a la Aceptación y Publicación masiva.',
      docente: 'Verifica tus notas cargadas con detenimiento: al ser Aceptadas por el directivo perderás el permiso de edición.',
      tutor: 'Las calificaciones aparecerán visibles únicamente cuando hayan sido Aceptadas y Publicadas por la institución.'
    }
  },
  {
    patrones: ['/Reportes'],
    titulo: 'Reportes e Impresión de Listados',
    subtitulo: 'Consulta, exportación a Excel e impresión por curso',
    icono: 'fa-chart-pie',
    resumen: 'Sección especializada para consultar los listados de estudiantes organizados por curso, con opción de exportarlos directamente a planillas de Excel o enviarlos a imprimir.',
    funcionalidades: [
      {
        titulo: 'Nómina por Cursos',
        descripcion: 'Genera el listado actualizado de estudiantes matriculados en cada comisión.',
        icono: 'fa-clipboard-user'
      },
      {
        titulo: 'Exportar a Excel',
        descripcion: 'Descarga las listas de alumnos en formato informático editable .xlsx.',
        icono: 'fa-file-excel'
      },
      {
        titulo: 'Impresión Directa',
        descripcion: 'Formato optimizado para imprimir planillas físicas de asistencia y seguimiento.',
        icono: 'fa-print'
      }
    ],
    consejosPorRol: {
      admin: 'Utiliza la exportación a Excel para generar respaldos de nóminas de inicio de ciclo.',
      preceptor: 'Imprime directamente los listados de curso para la toma física de asistencia diaria.'
    }
  },
  {
    patrones: ['/Promocion'],
    titulo: 'Promoción de Estudiantes',
    subtitulo: 'Pase masivo de alumnos de un ciclo lectivo al siguiente',
    icono: 'fa-graduation-cap',
    resumen: 'Herramienta para promover masivamente a los estudiantes al siguiente curso o año lectivo. Requisito fundamental: Es necesario tener previamente creada la próxima cohorte para poder efectuar la promoción masiva.',
    funcionalidades: [
      {
        titulo: 'Requisito de Próxima Cohorte',
        descripcion: 'Verifica que la cohorte de destino esté dadas de alta en /Cursos antes de comenzar.',
        icono: 'fa-triangle-exclamation'
      },
      {
        titulo: 'Promoción Masiva',
        descripcion: 'Selecciona la división de origen y transfiere los alumnos promovidos en un solo paso.',
        icono: 'fa-arrow-up-right-dots'
      }
    ],
    consejosPorRol: {
      admin: 'Si no encuentras la cohorte de destino en el desplegable, ve primero a /Cursos y créala.'
    }
  },
  {
    patrones: ['/AdmMensajes'],
    titulo: 'Administración de Mensajería y Reglas',
    subtitulo: 'Configuración de permisos de comunicación entre roles',
    icono: 'fa-envelopes-bulk',
    resumen: 'Módulo de administración global de mensajería (diferente a la casilla personal /Mensajes). Permite administrar los mensajes de los usuarios y configurar las reglas institucionales que definen qué rol le puede escribir a qué otro rol.',
    funcionalidades: [
      {
        titulo: 'Matriz de Permisos por Rol',
        descripcion: 'Define qué roles (ej. Tutor -> Docente, Estudiante -> Preceptor) pueden iniciar chats.',
        icono: 'fa-sliders'
      },
      {
        titulo: 'Auditoría e Historial',
        descripcion: 'Administra y gestiona los mensajes del sistema por requerimiento institucional.',
        icono: 'fa-shield-halved'
      }
    ],
    consejosPorRol: {
      admin: 'Restringe la mensajería directa entre alumnos si prefieres centralizar la comunicación por canales de aula.'
    }
  },
  {
    patrones: ['/Mensajes', '/Chat'],
    titulo: 'Mensajería Interna y Chats',
    subtitulo: 'Bandeja de entrada personal y modalidades de mensaje',
    icono: 'fa-envelope',
    resumen: 'Tu casilla personal de comunicación del sistema. Permite interactuar mediante tres modalidades de conversación: Mensajería Directa (MD), Mensajería Grupal y Mensajería de Difusión.',
    funcionalidades: [
      {
        titulo: 'Mensajería Directa (MD)',
        descripcion: 'Comunicación privada directa entre dos personas (uno a uno).',
        icono: 'fa-user-group'
      },
      {
        titulo: 'Mensajería Grupal',
        descripcion: 'Conversaciones para comisiones o grupos. Según la configuración del grupo, se puede escribir entre todos los miembros o únicamente administradores/docentes.',
        icono: 'fa-comments'
      },
      {
        titulo: 'Mensajería de Difusión',
        descripcion: 'Envío de comunicados desde un usuario a múltiples destinatarios con el único fin de difundir una novedad (sin respuestas cruzadas).',
        icono: 'fa-bullhorn'
      }
    ],
    porRol: {
      docente: {
        titulo: 'Mensajería Interna — Panel Docente',
        subtitulo: 'Mensajería directa, grupos y difusión con alumnos y tutores',
        resumen: 'Casilla de mensajería para comunicarte con tus cursos y alumnos. Puedes enviar Mensajería Directa (MD) a un estudiante/tutor, interactuar en grupos o emitir mensajes de Difusión.',
        funcionalidades: [
          {
            titulo: 'Mensajería Directa (MD)',
            descripcion: 'Diálogo privado entre dos personas para consultas individuales.',
            icono: 'fa-user-group'
          },
          {
            titulo: 'Mensajería Grupal',
            descripcion: 'Canales grupales del curso donde se puede habilitar la participación de todos o restringirla a docentes.',
            icono: 'fa-comments'
          },
          {
            titulo: 'Mensajería de Difusión',
            descripcion: 'Envía anuncios unidireccionales a múltiples estudiantes o tutores.',
            icono: 'fa-bullhorn'
          }
        ]
      }
    },
    consejosPorRol: {
      docente: 'Utiliza la mensajería de difusión para avisos generales e inicia MD para atención personalizada.',
      estudiante: 'Envía un mensaje directo (MD) a tu docente ante consultas personales de cursada.',
      admin: 'Recuerda que en /AdmMensajes puedes configurar los permisos para cada tipo de mensajería.'
    }
  },
  {
    patrones: ['/Notificaciones'],
    titulo: 'Centro de Notificaciones',
    subtitulo: 'Historial de alertas y avisos del sistema',
    icono: 'fa-bell',
    resumen: 'Registro de todas las alertas del sistema, incluyendo publicaciones de notas, nuevos materiales y respuestas.',
    funcionalidades: [
      {
        titulo: 'Historial Completo',
        descripcion: 'Revisa los avisos recientes agrupados por fecha y tipo.',
        icono: 'fa-list-check'
      },
      {
        titulo: 'Marcar como Leídas',
        descripcion: 'Limpia tu contador de notificaciones con un solo clic.',
        icono: 'fa-check-double'
      }
    ]
  },
  {
    patrones: ['/Estadisticas', '/Monitoreo'],
    titulo: 'Monitoreo y Estadística Institucional',
    subtitulo: 'Indicadores clave y análisis de datos para la toma de decisiones',
    icono: 'fa-chart-line',
    resumen: 'Consola estratégica de monitoreo y estadísticas institucionales. Diseñada para administradores, directivos y secretaría con filtros generales por Formación, Año/Orden y División.',
    funcionalidades: [
      {
        titulo: 'Filtro General Global',
        descripcion: 'Filtra todas las métricas, tarjetas y gráficos por Formación, Año u Orden y División.',
        icono: 'fa-filter'
      },
      {
        titulo: 'Estudiantes en Riesgo Académico',
        descripcion: 'Identifica alumnos con espacios curriculares en riesgo (nota < 6 o umbral valorativo seleccionado) junto a su nivel de asistencia.',
        icono: 'fa-triangle-exclamation'
      },
      {
        titulo: 'Estudiantes con más Inasistencias',
        descripcion: 'Ranking de estudiantes con mayor cantidad de ausencias para intervención preventiva.',
        icono: 'fa-user-xmark'
      },
      {
        titulo: 'Gráfico por Espacio Curricular',
        descripcion: 'Gráfico de barras detallado que muestra la cantidad de estudiantes en riesgo por cada materia o espacio curricular.',
        icono: 'fa-chart-bar'
      }
    ]
  },
  {
    patrones: ['/Agenda'],
    titulo: 'Agenda y Calendario de Eventos',
    subtitulo: 'Calendario de actividades y compromisos institucionales',
    icono: 'fa-calendar-days',
    resumen: 'La Agenda permite consultar la programación de eventos institucionales, fechas de mesas de examen, entregas de trabajos, reuniones de tutores y recesos lectivos del establecimiento.',
    funcionalidades: [
      {
        titulo: 'Calendario de Eventos',
        descripcion: 'Visualización cronológica de eventos, convocatorias y feriados.',
        icono: 'fa-calendar-week'
      },
      {
        titulo: 'Filtros por Tipo de Recordatorio',
        descripcion: 'Filtra avisos por categoría o destinatarios.',
        icono: 'fa-filter'
      },
      {
        titulo: 'Recordatorios',
        descripcion: 'Seguimiento de fechas clave para el dictado y cursado.',
        icono: 'fa-clock'
      }
    ],
    porRol: {
      docente: {
        titulo: 'Agenda y Calendario — Panel Docente',
        subtitulo: 'Calendario de eventos, convocatorias y fechas clave',
        resumen: 'La Agenda te permite consultar el calendario de eventos institucionales, fechas de mesas de examen, entregas de trabajos y reuniones docentes programadas para el ciclo lectivo.',
        funcionalidades: [
          {
            titulo: 'Calendario de Eventos Institucionales',
            descripcion: 'Muestra los eventos, reuniones y recesos agendados en la institución.',
            icono: 'fa-calendar-days'
          },
          {
            titulo: 'Mesas de Examen y Entregas',
            descripcion: 'Seguimiento de fechas límite de evaluaciones y entregas.',
            icono: 'fa-clipboard-check'
          }
        ]
      }
    }
  },
  {
    patrones: ['/Horarios'],
    titulo: 'Horarios de Cursado',
    subtitulo: 'Distribución horaria por curso y espacio curricular',
    icono: 'fa-clock',
    resumen: 'Grilla semanal de carga horaria, aulas físicas asignadas y turnos de cursado.',
    funcionalidades: [
      {
        titulo: 'Grilla Semanal',
        descripcion: 'Visualiza la distribución de horas por día de la semana.',
        icono: 'fa-calendar-week'
      },
      {
        titulo: 'Filtro por Curso / Aula',
        descripcion: 'Encuentra rápidamente la disponibilidad de salones o comisiones.',
        icono: 'fa-filter'
      }
    ]
  },
  {
    patrones: ['/Cuotas'],
    titulo: 'Gestión de Cuotas y Aranceles',
    subtitulo: 'Administración de Tesorería y pagos',
    icono: 'fa-money-bill-1',
    resumen: 'Módulo financiero para consultar el estado de cuenta arancelario, registrar pagos y emitir comprobantes.',
    funcionalidades: [
      {
        titulo: 'Estado de Cuenta',
        descripcion: 'Consulta los vencimientos, saldos adeudados y pagos al día.',
        icono: 'fa-receipt'
      },
      {
        titulo: 'Registro de Cobros',
        descripcion: 'Asigna pagos y emite recibos institucionales.',
        icono: 'fa-hand-holding-dollar'
      }
    ]
  },
  {
    patrones: ['/Perfil', '/Pass'],
    titulo: 'Mi Perfil y Seguridad',
    subtitulo: 'Datos personales y configuración de cuenta',
    icono: 'fa-user-pen',
    resumen: 'Gestión de tus datos de usuario, correos de contacto, foto de perfil y cambio de contraseña.',
    funcionalidades: [
      {
        titulo: 'Actualizar Correos',
        descripcion: 'Asegura la recepción de alertas añadiendo tus mails de contacto.',
        icono: 'fa-envelope-open-text'
      },
      {
        titulo: 'Seguridad',
        descripcion: 'Cambia tu clave de acceso periódicamente para proteger tu cuenta.',
        icono: 'fa-key'
      }
    ]
  }
];

/**
 * Guía predeterminada cuando la ruta no coincide explícitamente con ningún patrón específico.
 */
export const GUIA_DEFAULT = {
  titulo: 'Ayuda Contextual tresaedu',
  subtitulo: 'Navegación y funciones de la plataforma',
  icono: 'fa-circle-question',
  resumen: 'Te encuentras navegando en la plataforma educativa tresaedu. Desde el menú lateral y la barra superior puedes acceder a todas las funcionalidades principales.',
  funcionalidades: [
    {
      titulo: 'Menú Lateral',
      descripcion: 'Accede a tus cursos, materias, agenda, notas y administración.',
      icono: 'fa-bars'
    },
    {
      titulo: 'Barra Superior',
      descripcion: 'Consulta tus mensajes directos, notificaciones y opciones de perfil.',
      icono: 'fa-heading'
    },
    {
      titulo: 'Auto-Ayuda',
      descripcion: 'Puedes activar o desactivar la apertura automática de este modal en el pie del diálogo.',
      icono: 'fa-sliders'
    }
  ],
  consejosPorRol: {
    admin: 'Si requieres asistencia técnica adicional, comunícate con el soporte del sistema.',
    docente: 'Recuerda guardar periódicamente tus avances al redactar clases o cargar notas.',
    estudiante: 'Ante cualquier inquietud sobre la cursada, puedes enviar un mensaje a tus profesores.',
    preceptor: 'Utiliza los filtros por división para agilizar el control de asistencias.'
  }
};

/**
 * Mapea el ID de rol numérico a la clave descriptiva correspondiente según el listado oficial del sistema.
 * 1: super_admin
 * 2: administrador
 * 3: equipo_directivo
 * 4: secretaria
 * 5: auxiliar
 * 6: docente
 * 7: estudiante
 * 8: tutor
 * 9: docentes_especiales
 * 11: agente_externo
 * 12: maestranza
 * 13: tesorero
 */
export function obtenerClaveRol(id) {
  if (id === null || id === undefined || id === '') return 'estudiante';
  const rId = String(id).trim();
  switch (rId) {
    case '1': return 'super_admin';
    case '2': return 'administrador';
    case '3': return 'equipo_directivo';
    case '4': return 'secretaria';
    case '5': return 'auxiliar';
    case '6': return 'docente';
    case '7': return 'estudiante';
    case '8': return 'tutor';
    case '9': return 'docentes_especiales';
    case '11': return 'agente_externo';
    case '12': return 'maestranza';
    case '13': return 'tesorero';
    default: return 'estudiante';
  }
}

/**
 * Retorna las claves de alias compatibles en orden jerárquico de fallback.
 */
export function obtenerAliasesRol(claveRol) {
  switch (claveRol) {
    case 'super_admin':
      return ['super_admin', 'administrador', 'equipo_directivo', 'admin'];
    case 'administrador':
      return ['administrador', 'super_admin', 'equipo_directivo', 'admin'];
    case 'equipo_directivo':
      return ['equipo_directivo', 'administrador', 'super_admin', 'admin'];
    case 'docente':
      return ['docente', 'docentes_especiales'];
    case 'docentes_especiales':
      return ['docentes_especiales', 'docente'];
    case 'secretaria':
      return ['secretaria', 'preceptor', 'auxiliar'];
    case 'auxiliar':
      return ['auxiliar', 'secretaria', 'preceptor'];
    case 'tutor':
      const tieneTutelado = typeof window !== 'undefined' && localStorage.getItem('kte');
      return tieneTutelado ? ['tutor_tutelado', 'tutor'] : ['tutor'];
    case 'tesorero':
      return ['tesorero', 'tesoreria'];
    case 'estudiante':
      return ['estudiante'];
    case 'agente_externo':
      return ['agente_externo', 'estudiante'];
    case 'maestranza':
      return ['maestranza', 'auxiliar'];
    default:
      return [claveRol, 'estudiante'];
  }
}

/**
 * Obtiene la ayuda contextual correspondiente a una ruta y rol específicos.
 * @param {string} pathname - Ruta actual (ej: "/Principal", "/MC/15", "/Usuarios")
 * @param {string|number} rolId - ID del rol activo del usuario
 * @returns {Object} Objeto de ayuda estructurado.
 */
export function obtenerAyudaPorRuta(pathname = '', rolId = null) {
  if (!pathname) return GUIA_DEFAULT;

  const rutaLimpia = pathname.trim();
  const claveRol = obtenerClaveRol(rolId);
  const aliases = obtenerAliasesRol(claveRol);

  let guiaEncontrada = null;

  // Buscar coincidencia exacta, por comodín * o por inicio de ruta
  for (const guia of AYUDAS_POR_RUTA) {
    for (const patron of guia.patrones) {
      const patronLower = patron.toLowerCase();
      const rutaLower = rutaLimpia.toLowerCase();

      let coincide = false;
      if (patronLower.includes('*')) {
        const regexStr = '^' + patronLower.replace(/[.+^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '[^/]+') + '(?:/.*)?$';
        const regex = new RegExp(regexStr);
        coincide = regex.test(rutaLower);
      } else {
        coincide = (
          rutaLower === patronLower ||
          (patronLower !== '/' && rutaLower.startsWith(patronLower))
        );
      }

      if (coincide) {
        guiaEncontrada = guia;
        break;
      }
    }
    if (guiaEncontrada) break;
  }

  if (!guiaEncontrada) guiaEncontrada = GUIA_DEFAULT;

  // Si la guía contiene variantes específicas por rol
  if (guiaEncontrada.porRol) {
    let varianteRol = null;
    for (const alias of aliases) {
      if (guiaEncontrada.porRol[alias]) {
        varianteRol = guiaEncontrada.porRol[alias];
        break;
      }
    }

    if (varianteRol) {
      return {
        ...guiaEncontrada,
        titulo: varianteRol.titulo || guiaEncontrada.titulo,
        subtitulo: varianteRol.subtitulo || guiaEncontrada.subtitulo,
        resumen: varianteRol.resumen || guiaEncontrada.resumen,
        funcionalidades: varianteRol.funcionalidades || guiaEncontrada.funcionalidades,
        pasosIniciales: varianteRol.hasOwnProperty('pasosIniciales') ? varianteRol.pasosIniciales : guiaEncontrada.pasosIniciales,
        procesoCalificacion: varianteRol.hasOwnProperty('procesoCalificacion') ? varianteRol.procesoCalificacion : guiaEncontrada.procesoCalificacion,
      };
    }
  }

  return guiaEncontrada;
}
